import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBeforeUnload } from 'react-router-dom';

import {
  type CardDisputeDocumentEvidenceInput,
  CardDisputeEligibilityReason,
  cardsGetCardTransactionOptions,
  cardsListCardDisputesOptions,
} from '@column/api';
import { Button, formatNumber } from '@column/column-ui-kit';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Checkbox,
  Label,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@column/ui';

import { DisputeEvidenceComposer } from '../Evidence/DisputeEvidenceComposer';
import { IssuerDashboardGate } from '../IssuerDashboardGate';
import { UnauthorizedState } from '~/app/_components';
import { ROUTE } from '~/app/routes';
import { Loading } from '~/components';
import { useDebounce } from '~/hooks/useDebounce';
import { useQueryParams } from '~/hooks/useQueryParams';
import {
  apiErrorDetail,
  apiErrorMessage,
  apiErrorType,
  buildCardDisputesListQuery,
  useCanManageCardDisputes,
  useCreateCardDispute,
} from '~/lib/api/cardDisputes';
import {
  CARD_DISPUTE_ELIGIBILITY_REASON_LABELS,
  CARD_DISPUTE_REASON_GROUPS,
  CARD_DISPUTE_REASON_LABELS,
  type CardDisputeReason,
  getWorkflowLabel,
} from '~/lib/disputes/labels';
import { useNavigate } from '~/lib/navigation';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { formatDateTime } from '~/util';

import { DisputeReasonForm } from './DisputeReasonForm';
import {
  type DisputeRequirementContext,
  NARRATIVE_TOO_LONG_MESSAGE,
  REASON_REQUIRES_NARRATIVE,
  REASON_WORKFLOW,
  buildCreateDisputeRequest,
  buildEligibilityRequest,
  disputeAmountHardStop,
  disputeRequirementContext,
  disputeSubmissionReadiness,
  emptyReasonFormState,
  evidenceGuidance,
  findReasonHardStops,
  formatSmallestUnitToMajorUnits,
  isPartialClaim,
  missingReasonFields,
  narrativeTooLong,
  parseMajorUnitsToSmallestUnit,
  questionnaireAnswers,
  reasonForPartialDisputeTooLong,
  reasonNotApplicableMessage,
  reasonRequiresPartialExplanation,
  reasonSupportsProvisionalCredit,
} from './questionnaire';
import { ReasonEvidenceGuidance, ReasonField } from './ReasonFields';
import { useCardDisputeEligibility } from './useCardDisputeEligibility';

type WizardStep = 'transaction' | 'claim' | 'evidence' | 'review';

interface NewDisputeQueryParams {
  transaction_id?: string;
}

const workflowLabels = (value: string) => getWorkflowLabel(value);

export const PageCardDisputeNew: React.FC = () => {
  const canManage = useCanManageCardDisputes();
  const platformId = useSessionStore((state) => state.currentPlatform?.id);
  const isSandbox = useSessionStore((state) => state.isSandbox);

  return (
    <IssuerDashboardGate>
      {/* Remounting on platform/mode change resets every in-memory answer. */}
      {canManage ? (
        <PageCardDisputeNewContent key={`${platformId ?? ''}:${isSandbox ? 'sandbox' : 'live'}`} />
      ) : (
        <UnauthorizedState />
      )}
    </IssuerDashboardGate>
  );
};

const PageCardDisputeNewContent: React.FC = () => {
  const navigate = useNavigate();
  const { addDangerNotification } = useNotificationStore();
  const { queryParams } = useQueryParams<NewDisputeQueryParams>();

  const [step, setStep] = useState<WizardStep>(queryParams?.transaction_id ? 'claim' : 'transaction');
  const [transactionIdInput, setTransactionIdInput] = useState(queryParams?.transaction_id ?? '');
  const [transactionId, setTransactionId] = useState(queryParams?.transaction_id ?? '');
  const [lookupTransaction] = useDebounce((value: string) => setTransactionId(value), 300);
  const [reason, setReason] = useState<CardDisputeReason | ''>('');
  const [reasonState, setReasonState] = useState(emptyReasonFormState);
  const [claimAmount, setClaimAmount] = useState('');
  const [partialExplanation, setPartialExplanation] = useState('');
  const [narrative, setNarrative] = useState('');
  const [evidence, setEvidence] = useState<CardDisputeDocumentEvidenceInput[]>([]);
  const [wantsCredit, setWantsCredit] = useState(false);
  const [creditConfirmed, setCreditConfirmed] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [createError, setCreateError] = useState<string | undefined>();

  const eligibility = useCardDisputeEligibility();
  const createMutation = useCreateCardDispute();

  const transactionQuery = useQuery({
    ...cardsGetCardTransactionOptions({ path: { card_transaction_id: transactionId } }),
    enabled: Boolean(transactionId),
    retry: false,
  });
  const transaction = transactionId === transactionIdInput.trim() ? transactionQuery.data : undefined;

  const activeDisputesQuery = useQuery({
    ...cardsListCardDisputesOptions({
      query: buildCardDisputesListQuery({ query: { card_transaction_id: transactionId || undefined }, pageSize: 25 }),
    }),
    enabled: Boolean(transactionId),
  });
  const activeDispute = activeDisputesQuery.data?.card_disputes.find(
    (entry) =>
      entry.card_transaction_id === transactionId && entry.stage !== 'dispute_won' && entry.stage !== 'dispute_close'
  );

  const settled = transaction?.total_amounts?.cardholder_settled;
  // Settled debits are stored negative; the claim amount is positive, in the billing currency
  // derived from the cardholder settled amount — never the merchant transaction currency.
  const settledSmallest = settled ? Math.abs(settled.amount) : undefined;
  const currency = settled?.currency_code ?? '';

  const claimSmallest = currency ? parseMajorUnitsToSmallestUnit(claimAmount, currency) : undefined;
  const claimPartial =
    claimSmallest !== undefined && settledSmallest !== undefined && isPartialClaim(claimSmallest, settledSmallest);
  // incorrect_amount is partial by nature, so its partial claims carry no explanation.
  const partialExplanationRequired = claimPartial && reason !== '' && reasonRequiresPartialExplanation(reason);
  // Eligibility measures a new claim against the cardholder settled total.
  const claimAmountHardStop =
    reason === ''
      ? undefined
      : disputeAmountHardStop({
          reason,
          claimAmount: claimSmallest,
          transactionAmount: settledSmallest,
          formState: reasonState,
          currency,
        });
  const narrativeOverLimit = narrativeTooLong(narrative);
  const requirementContext = useMemo(() => disputeRequirementContext(transaction), [transaction]);
  const missingFields = reason ? missingReasonFields(reason, reasonState, currency, requirementContext) : [];
  const hardStops = reason ? findReasonHardStops(reason, reasonState, currency, requirementContext) : [];
  const reasonNotApplicable = reason ? reasonNotApplicableMessage(reason, requirementContext.transaction) : undefined;

  const hasUnsavedWork =
    Boolean(reason) ||
    claimAmount !== '' ||
    partialExplanation !== '' ||
    narrative !== '' ||
    evidence.length > 0 ||
    wantsCredit;
  // Set once the draft exists so a retry after a saved-but-unnavigated create cannot double-create.
  const savedDisputeRef = useRef<string | undefined>(undefined);
  // The app uses a plain BrowserRouter (no data router), so useBlocker is unavailable — it throws
  // at render. The browser-native guard covers the data-loss-prone paths: closing or refreshing
  // the tab with answers still in memory.
  useBeforeUnload(
    useCallback(
      (event: BeforeUnloadEvent) => {
        if (hasUnsavedWork && !savedDisputeRef.current) {
          event.preventDefault();
          event.returnValue = 'Leave with an unsaved dispute? Your answers will be lost.';
        }
      },
      [hasUnsavedWork]
    )
  );

  useEffect(() => {
    if (transactionQuery.error) {
      addDangerNotification({ content: 'Card transaction not found. Check the ID and try again.' });
      setStep('transaction');
    }
  }, [transactionQuery.error, addDangerNotification]);

  // Prefill the claim with the settled amount once per transaction, whichever entry path loaded it
  // (transaction lookup or the deep link from transaction detail). Never overwrites user input.
  const prefilledTransactionRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!transaction || !settledSmallest || !currency || prefilledTransactionRef.current === transaction.id) {
      return;
    }
    prefilledTransactionRef.current = transaction.id;
    setClaimAmount((current) => current || formatSmallestUnitToMajorUnits(settledSmallest, currency));
  }, [transaction, settledSmallest, currency]);

  const handleTransactionIdChange = (value: string) => {
    setTransactionIdInput(value);
    if (value.trim() === transactionIdInput.trim()) {
      return;
    }
    eligibility.reset();
    setShowValidation(false);
    setCreateError(undefined);
    setClaimAmount('');
    prefilledTransactionRef.current = undefined;
    setTransactionId('');
    lookupTransaction(value.trim());
  };

  const canContinueWithTransaction =
    Boolean(transaction && settledSmallest && currency) && transactionQuery.isSuccess && !transactionQuery.isFetching;

  const handleTransactionConfirmed = () => {
    if (!canContinueWithTransaction) {
      return;
    }
    setStep('claim');
  };

  const claimBlocked =
    !reason ||
    claimSmallest === undefined ||
    claimSmallest <= 0 ||
    Boolean(claimAmountHardStop) ||
    Boolean(reasonNotApplicable) ||
    hardStops.length > 0 ||
    missingFields.length > 0;

  const handleClaimContinue = async () => {
    if (claimBlocked || !reason || claimSmallest === undefined) {
      setShowValidation(true);
      return;
    }

    const result = await eligibility.check(
      buildEligibilityRequest({
        cardTransactionId: transactionId,
        amount: claimSmallest,
        currency,
        reason,
        formState: reasonState,
        context: requirementContext,
        evidence: [],
      })
    );
    if (!result) {
      return;
    }
    // missing_required_evidence does not block: narrative and documents are collected next, and a
    // draft may be saved without them. Every other reported reason is a hard blocker.
    const blockers = result.reasons.filter((entry) => entry !== CardDisputeEligibilityReason.MISSING_REQUIRED_EVIDENCE);
    if (blockers.length > 0) {
      return;
    }
    setStep('evidence');
  };

  const handleReviewContinue = () => {
    if (narrativeOverLimit) {
      return;
    }
    if (!reason || missingFields.length > 0 || hardStops.length > 0) {
      setShowValidation(true);
      setStep('claim');
      return;
    }
    setStep('review');
  };

  const handleSaveDraft = async () => {
    if (
      !reason ||
      claimSmallest === undefined ||
      !currency ||
      hardStops.length > 0 ||
      narrativeOverLimit ||
      (wantsCredit && !creditConfirmed)
    ) {
      return;
    }

    const request = buildCreateDisputeRequest({
      cardTransactionId: transactionId,
      claimAmount: claimSmallest,
      currency,
      reason,
      formState: reasonState,
      context: requirementContext,
      narrative,
      evidence,
      partial: claimPartial,
      partialExplanation,
      issueProvisionalCredit: wantsCredit,
    });

    setCreateError(undefined);
    try {
      const dispute = await createMutation.mutateAsync({ request, transactionId });
      savedDisputeRef.current = dispute.id;
      navigate(`${ROUTE.CARD_DISPUTES}/edit/${dispute.id}`);
    } catch (error) {
      if (apiErrorType(error) === 'transfer_non_sufficient_fund') {
        // Funding failures keep the form and the selected financial request untouched.
        setCreateError(
          'The program operating account does not have enough funds to issue the provisional credit. The dispute was not created.'
        );
        return;
      }
      // The server's already-disputed guard carries the winning dispute's ID: a concurrent create
      // (or a dispute that raced us) saved the record. Navigate to it instead of creating a
      // duplicate. Any other failure keeps the user on the form with the real error.
      const winningDisputeId = apiErrorDetail(error, 'card_dispute_id');
      if (winningDisputeId) {
        savedDisputeRef.current = winningDisputeId;
        navigate(`${ROUTE.CARD_DISPUTES}/edit/${winningDisputeId}`);
        return;
      }
      setCreateError(apiErrorMessage(error, 'The dispute was not saved. Check for errors and try again.'));
    }
  };

  const transactionSummary = transaction && settled && (
    <div
      className="grid grid-cols-1 gap-3 rounded-lg border border-border bg-card p-4 sm:grid-cols-4"
      data-testid="new-dispute-transaction-summary"
    >
      <SummaryItem label="Merchant">{transaction.card_acceptor_details?.name || '—'}</SummaryItem>
      <SummaryItem label="Date">{formatDateTime(transaction.created_at, { timeZoneFormat: 'local' })}</SummaryItem>
      <SummaryItem label="Cardholder settled amount">{formatNumber(settled.amount, settled.currency_code)}</SummaryItem>
      <SummaryItem label="Billing currency">{settled.currency_code}</SummaryItem>
    </div>
  );

  return (
    <Inner px="var(--page-margin)" pb={16} data-testid={`new-dispute-${step}-step`}>
      {step === 'transaction' && (
        <div className="mx-auto flex max-w-xl flex-col gap-4 py-8">
          {transactionSummary}
          <ReasonField
            label="Card transaction ID"
            htmlFor="new-dispute-transaction-id"
            required
            description="Paste the settled card transaction ID to look it up automatically."
          >
            <input
              id="new-dispute-transaction-id"
              className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
              value={transactionIdInput}
              placeholder="ctxn_..."
              data-dd-privacy="hidden"
              data-testid="new-dispute-transaction-id"
              onChange={(event) => handleTransactionIdChange(event.target.value)}
            />
          </ReasonField>

          {activeDispute && (
            <Alert data-testid="new-dispute-existing-alert">
              <AlertTitle>This transaction already has an active dispute</AlertTitle>
              <AlertDescription>
                <a
                  className="text-primary underline"
                  href={`${ROUTE.CARD_DISPUTES}/edit/${activeDispute.id}`}
                  data-testid="new-dispute-existing-link"
                >
                  Open the existing dispute
                </a>
              </AlertDescription>
            </Alert>
          )}

          <div className="flex justify-end">
            <Button
              onClick={handleTransactionConfirmed}
              variant={canContinueWithTransaction ? 'primary' : 'secondary'}
              isDisabled={!canContinueWithTransaction}
              isLoading={transactionQuery.isFetching}
            >
              Continue
            </Button>
          </div>
        </div>
      )}

      {step !== 'transaction' && !transaction && (
        <Loading isLoading={transactionQuery.isLoading} fullHeight>
          <div className="py-8" />
        </Loading>
      )}

      {step !== 'transaction' && transaction && settled && currency && (
        <div className="flex flex-col gap-6 py-6">
          {transactionSummary}

          {activeDispute && (
            <Alert data-testid="new-dispute-existing-alert">
              <AlertTitle>This transaction already has an active dispute</AlertTitle>
              <AlertDescription>
                <a className="text-primary underline" href={`${ROUTE.CARD_DISPUTES}/edit/${activeDispute.id}`}>
                  Open the existing dispute
                </a>
              </AlertDescription>
            </Alert>
          )}

          {step === 'claim' && (
            <div className="flex max-w-2xl flex-col gap-6">
              <ReasonField label="Reason" required description="The reason is permanent once the dispute is created.">
                <Select
                  value={reason}
                  onValueChange={(next) => {
                    const nextReason = next as CardDisputeReason;
                    // Changing the reason never carries another reason's answers forward.
                    setReason(nextReason);
                    setReasonState(emptyReasonFormState());
                    if (!reasonSupportsProvisionalCredit(nextReason)) {
                      setWantsCredit(false);
                      setCreditConfirmed(false);
                    }
                    eligibility.reset();
                    setShowValidation(false);
                  }}
                >
                  <SelectTrigger className="w-full" data-testid="new-dispute-reason" aria-label="Dispute reason">
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent position="popper" align="end" data-dd-privacy="hidden">
                    {CARD_DISPUTE_REASON_GROUPS.map((group) => (
                      <SelectGroup key={group.label}>
                        <SelectLabel>{group.label}</SelectLabel>
                        {group.reasons.map((option) => (
                          <SelectItem key={option} value={option}>
                            {CARD_DISPUTE_REASON_LABELS[option]}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
                {reasonNotApplicable && (
                  <p className="text-xs text-destructive" data-testid="new-dispute-reason-not-applicable">
                    {reasonNotApplicable}
                  </p>
                )}
              </ReasonField>

              <ReasonField
                label={`Claim amount (${currency})`}
                required
                description={`In the cardholder's billing currency. Up to ${formatNumber(settledSmallest ?? 0, currency)}.`}
              >
                <input
                  className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                  value={claimAmount}
                  inputMode="decimal"
                  data-dd-privacy="hidden"
                  data-testid="new-dispute-claim-amount"
                  onChange={(event) => setClaimAmount(event.target.value)}
                />
                {showValidation && claimAmount !== '' && claimSmallest === undefined && (
                  <p className="text-xs text-destructive" data-testid="new-dispute-claim-amount-error">
                    Enter a valid amount in {currency}.
                  </p>
                )}
                {claimAmountHardStop && (
                  <p className="text-xs text-destructive" data-testid="new-dispute-claim-amount-error">
                    {claimAmountHardStop}
                  </p>
                )}
              </ReasonField>

              {partialExplanationRequired && (
                <ReasonField
                  label="Why is the claim less than the full transaction amount?"
                  required
                  description={'Required when disputing a partial amount. Up to 10,000 characters.'}
                >
                  <Textarea
                    value={partialExplanation}
                    disabled={false}
                    data-dd-privacy="hidden"
                    data-testid="new-dispute-partial-explanation"
                    onChange={(event) => setPartialExplanation(event.target.value)}
                  />
                  {reasonForPartialDisputeTooLong(partialExplanation) && (
                    <p className="text-xs text-destructive">The explanation is limited to 10,000 characters.</p>
                  )}
                </ReasonField>
              )}

              {reason && (
                <DisputeReasonForm
                  reason={reason}
                  value={reasonState}
                  onChange={setReasonState}
                  currency={currency}
                  context={requirementContext}
                  missingFields={showValidation ? missingFields : []}
                />
              )}

              {eligibility.error && (
                <Alert variant="destructive">
                  <AlertTitle>Eligibility check failed</AlertTitle>
                  <AlertDescription>{eligibility.error}</AlertDescription>
                </Alert>
              )}

              {eligibility.eligibility && !eligibility.eligibility.eligible && (
                <Alert
                  variant={
                    eligibility.eligibility.reasons.some(
                      (entry) => entry !== CardDisputeEligibilityReason.MISSING_REQUIRED_EVIDENCE
                    )
                      ? 'destructive'
                      : 'default'
                  }
                  data-testid="new-dispute-eligibility-result"
                >
                  {eligibility.eligibility.reasons.some(
                    (entry) => entry !== CardDisputeEligibilityReason.MISSING_REQUIRED_EVIDENCE
                  ) ? (
                    <>
                      <AlertTitle>This transaction cannot be disputed right now</AlertTitle>
                      <AlertDescription>
                        <ul className="list-disc pl-4">
                          {eligibility.eligibility.reasons
                            .filter((entry) => entry !== CardDisputeEligibilityReason.MISSING_REQUIRED_EVIDENCE)
                            .map((entry) => (
                              <li key={entry}>{eligibilityReasonLabel(entry)}</li>
                            ))}
                        </ul>
                        {eligibility.eligibility?.existing_card_dispute_id && (
                          <a
                            className="text-primary underline"
                            href={`${ROUTE.CARD_DISPUTES}/edit/${eligibility.eligibility.existing_card_dispute_id}`}
                            data-testid="new-dispute-existing-link"
                          >
                            Open the existing dispute
                          </a>
                        )}
                      </AlertDescription>
                    </>
                  ) : (
                    <>
                      <AlertTitle>Required evidence is not complete yet</AlertTitle>
                      <AlertDescription>
                        The written statement and documents are collected in the next step. Saving a draft does not
                        require them; submitting does.
                      </AlertDescription>
                    </>
                  )}
                </Alert>
              )}

              <div className="flex justify-between">
                <Button variant="secondary" onClick={() => setStep('transaction')}>
                  Back
                </Button>
                <Button
                  onClick={() => void handleClaimContinue()}
                  isDisabled={claimBlocked || eligibility.isChecking}
                  isLoading={eligibility.isChecking}
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {step === 'evidence' && reason && (
            <div className="flex max-w-2xl flex-col gap-6">
              <div>
                <Label className="text-sm font-medium text-foreground" htmlFor="new-dispute-narrative">
                  Written statement
                </Label>
                <p className="mt-1 text-xs text-muted-foreground">
                  Describe the problem from the cardholder's perspective.{' '}
                  {REASON_REQUIRES_NARRATIVE[reason]
                    ? 'This reason requires a statement before submission.'
                    : 'Optional for this reason.'}{' '}
                  Up to 5,000 characters.
                </p>
                <Textarea
                  id="new-dispute-narrative"
                  className="mt-2"
                  value={narrative}
                  disabled={uploadBusy}
                  data-dd-privacy="hidden"
                  data-testid="new-dispute-narrative"
                  placeholder="Explain what happened"
                  onChange={(event) => setNarrative(event.target.value)}
                />
                {narrativeOverLimit && (
                  <p className="mt-1 text-xs text-destructive" data-testid="new-dispute-narrative-error">
                    {NARRATIVE_TOO_LONG_MESSAGE}
                  </p>
                )}
              </div>

              <ReasonEvidenceGuidance guidance={evidenceGuidance(reason, reasonState, currency, requirementContext)} />

              <DisputeEvidenceComposer value={evidence} onChange={setEvidence} onBusyChange={setUploadBusy} />

              <div className="flex justify-between">
                <Button variant="secondary" onClick={() => setStep('claim')}>
                  Back
                </Button>
                <Button onClick={handleReviewContinue} isDisabled={uploadBusy || narrativeOverLimit}>
                  Continue
                </Button>
              </div>
            </div>
          )}

          {step === 'review' && reason && claimSmallest !== undefined && (
            <ReviewStep
              currency={currency}
              reason={reason}
              requirementContext={requirementContext}
              claimAmount={claimSmallest}
              claimPartial={partialExplanationRequired}
              partialExplanation={partialExplanation}
              narrative={narrative}
              evidence={evidence}
              reasonState={reasonState}
              settledSmallest={settledSmallest ?? 0}
              wantsCredit={wantsCredit}
              creditConfirmed={creditConfirmed}
              onWantsCredit={setWantsCredit}
              onCreditConfirmed={setCreditConfirmed}
              onBack={() => setStep('evidence')}
              onSaveDraft={() => void handleSaveDraft()}
              saving={createMutation.isPending}
              createError={createError}
              readiness={disputeSubmissionReadiness({
                reason,
                formState: reasonState,
                currency,
                context: requirementContext,
                narrative,
                evidence,
                requiresPartialExplanation: partialExplanationRequired,
                partialExplanation,
              })}
            />
          )}
        </div>
      )}
    </Inner>
  );
};

const eligibilityReasonLabel = (reason: CardDisputeEligibilityReason): string =>
  (CARD_DISPUTE_ELIGIBILITY_REASON_LABELS as Record<string, string>)[reason] ?? reason;

interface ReviewStepProps {
  currency: string;
  reason: CardDisputeReason;
  requirementContext: DisputeRequirementContext;
  claimAmount: number;
  /** Whether the claim is partial and the reason needs the partial explanation. */
  claimPartial: boolean;
  partialExplanation: string;
  narrative: string;
  evidence: CardDisputeDocumentEvidenceInput[];
  reasonState: ReturnType<typeof emptyReasonFormState>;
  settledSmallest: number;
  wantsCredit: boolean;
  creditConfirmed: boolean;
  onWantsCredit: (value: boolean) => void;
  onCreditConfirmed: (value: boolean) => void;
  onBack: () => void;
  onSaveDraft: () => void;
  saving: boolean;
  createError?: string;
  readiness: { ready: boolean; missing: string[] };
}

const ReviewStep: React.FC<ReviewStepProps> = ({
  currency,
  reason,
  requirementContext,
  claimAmount,
  claimPartial,
  partialExplanation,
  narrative,
  evidence,
  reasonState,
  settledSmallest,
  wantsCredit,
  creditConfirmed,
  onWantsCredit,
  onCreditConfirmed,
  onBack,
  onSaveDraft,
  saving,
  createError,
  readiness,
}) => {
  const answers = questionnaireAnswers(reason, reasonState, currency, requirementContext);
  const creditSupported = reasonSupportsProvisionalCredit(reason);

  return (
    <div className="flex max-w-2xl flex-col gap-6" data-testid="new-dispute-review">
      <div className="grid grid-cols-1 gap-3 rounded-lg border border-border bg-card p-4 sm:grid-cols-2">
        <SummaryItem label="Reason">{CARD_DISPUTE_REASON_LABELS[reason]}</SummaryItem>
        <SummaryItem label="Workflow">{workflowLabels(REASON_WORKFLOW[reason])}</SummaryItem>
        <SummaryItem label="Claim amount">{formatNumber(claimAmount, currency)}</SummaryItem>
        <SummaryItem label="Settled transaction amount">{formatNumber(settledSmallest, currency)}</SummaryItem>
        <SummaryItem label="Billing currency">{currency}</SummaryItem>
        {answers.map((entry) => (
          <SummaryItem key={entry.field} label={entry.label}>
            {entry.answer}
          </SummaryItem>
        ))}
        <SummaryItem label="Written statement">{narrative.trim() ? narrative.trim() : '—'}</SummaryItem>
        <SummaryItem label="Documents">{evidence.length > 0 ? `${evidence.length} document(s)` : 'None'}</SummaryItem>
        {claimPartial && <SummaryItem label="Partial explanation">{partialExplanation.trim() || '—'}</SummaryItem>}
      </div>

      {!readiness.ready && (
        <Alert>
          <AlertTitle>A draft can be saved now; submitting needs more information</AlertTitle>
          <AlertDescription>
            Before submitting to the Card Network you will still need {readiness.missing.join(', ')}. You can complete
            these on the dispute after saving the draft.
          </AlertDescription>
        </Alert>
      )}

      <div
        className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4"
        data-testid="new-dispute-credit-section"
      >
        {creditSupported ? (
          <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-foreground">
            <Checkbox
              checked={wantsCredit}
              onCheckedChange={(checked) => onWantsCredit(checked === true)}
              data-testid="new-dispute-credit-toggle"
            />
            Issue provisional credit
          </label>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="new-dispute-credit-unsupported">
            Provisional credit is not available for this reason: the dispute returns funds the cardholder received.
          </p>
        )}
        {creditSupported && wantsCredit && (
          <label
            className="flex cursor-pointer items-start gap-2 rounded-md bg-muted/50 p-3 text-sm text-foreground"
            data-testid="new-dispute-credit-confirmation"
          >
            <Checkbox checked={creditConfirmed} onCheckedChange={(checked) => onCreditConfirmed(checked === true)} />
            <span>
              I understand the full dispute amount ({formatNumber(claimAmount, currency)}) will be credited to the
              cardholder immediately from the program operating account — including while this is only a saved draft.
              Credit does not wait for the Card Network to accept the dispute.
            </span>
          </label>
        )}
      </div>

      {createError && (
        <Alert variant="destructive" data-testid="new-dispute-create-error">
          <AlertTitle>The dispute was not saved</AlertTitle>
          <AlertDescription>{createError}</AlertDescription>
        </Alert>
      )}

      <div className="flex justify-between">
        <Button variant="secondary" onClick={onBack}>
          Back
        </Button>
        <Button
          onClick={onSaveDraft}
          isDisabled={saving || (creditSupported && wantsCredit && !creditConfirmed)}
          isLoading={saving}
        >
          Save draft
        </Button>
      </div>
    </div>
  );
};

const SummaryItem = ({ label, children }: React.PropsWithChildren<{ label: string }>) => (
  <div className="flex min-w-0 flex-col gap-1">
    <span className="text-xs font-medium text-muted-foreground">{label}</span>
    <span className="whitespace-pre-wrap break-words text-sm font-medium text-foreground">{children}</span>
  </div>
);
